/**
 * HOW_IT_WORKS — the four "how it works" steps, in one place because two surfaces read them:
 * the landing page renders them as cards, and `StructuredData` turns them into a `FAQPage`.
 *
 * 🔴 STEP 04 HAS NO BODY, DELIBERATELY (MARKETING-TRUTH-1, Reade's s216 ruling). Its body was
 * "Settlement in months, not years. Less spent on fees, more left for your family." — the second
 * of the two unsourced performance claims the ruling removed. The whole sentence went, not just
 * the two named clauses, because "more left for your family" is the same claim's third clause and
 * cannot stand without "Less spent on fees". No replacement copy was invented: writing a new
 * benefit line is a copy decision, and the ask's rails forbid copy beyond what it names.
 *
 * Consequences, both intended:
 *   - the card renders with its number and title and no body paragraph;
 *   - the `FAQPage` has THREE entries, not four — a step with no answer cannot be a Q&A.
 *
 * 🟢 The step-04 TITLE was "Settle in less time" — the same claim in compressed form, left in place
 * by the first pass because the removal ruling did not name it. RULED s216: it is now "Your executor
 * starts with everything", which describes the product rather than promising an outcome, so it needs
 * no source. The step still has no BODY, for the reason above.
 */

import { PRODUCT_NAME } from '@shared/brand';

export interface HowItWorksStep {
  num: string;
  title: string;
  /** The card's body paragraph, and the FAQ answer. Empty string = no body (see header). */
  body: string;
  /** The `FAQPage` question phrased from this step. Unused when `body` is empty. */
  question: string;
}

export const HOW_IT_WORKS: readonly HowItWorksStep[] = [
  {
    num: '01',
    title: 'Create your account',
    body: 'Sign up in minutes, free 30-day trial.',
    question: `How do I get started with ${PRODUCT_NAME}?`,
  },
  {
    num: '02',
    title: 'Organize your estate',
    body: 'Add assets, documents, contacts — upload a statement and watch it fill itself in.',
    question: 'How do I organize my estate?',
  },
  {
    num: '03',
    title: 'Prepare your executor',
    body: 'Government forms, notification letters, financial summaries pre-filled and ready.',
    question: `How does ${PRODUCT_NAME} prepare my executor?`,
  },
  {
    num: '04',
    // Ruled s216. Was "Settle in less time" — the removed performance claim in compressed form.
    // The replacement states what the product DOES and claims no outcome, so it needs no source.
    // No terminal period: its three siblings above carry none.
    title: 'Your executor starts with everything',
    body: '',
    question: '',
  },
] as const;
